// frontend/src/components/ReviewPage/FilterApplicabilityNote.tsx
//
// Row 23: loud cross-tile filter-applicability affordance. Pre-walk metadata (dims_view) —
// renders independently of the walk result, incl. alongside empty states; 'ok'/'unknown'
// render nothing. Lives INSIDE the export refs (pins/exports carry it; no data-export-skip).
import { Alert } from '@mui/material'
import {
  buildApplicabilityNoteLines,
  buildContextCaption,
  campaignRatioScopeNote,
  campaignRatioScopeWindowOnlyNote,
  type ApplicabilityNoteInput,
} from '~/components/Charts/chrome/chartChrome'
import { useFilterApplicability } from './useFilterApplicability'
import { useDimValueLabelFor } from './useDimValueLabelFor'
import type { FilterApplicability } from './filterApplicability'
import type { ReviewPageState } from './useReviewPageState'
import type { ActiveCampaign } from '~/state/useCampaignState'

/** C3b Task 5 (spec D-C3b-1): the honesty-note payload — a campaign's label + the attribution
 *  filters the walk ACTUALLY applied (I2/M8: the pruned/resolved set, NOT the raw predicate — a
 *  predicate dim dropped by the ratio union prune must not be claimed by the note). Empty when the
 *  campaign resolved window-only. Set via campaignRatioScopeFor. */
export interface CampaignRatioScope {
  campaignLabel: string
  /** The attribution dims the annotated walk actually fired with (union-pruned). Empty ⇒ the
   *  campaign contributed only its time window (rendered via campaignRatioScopeWindowOnlyNote). */
  appliedFilters: Record<string, string[]>
}

/** C3b Task 5: the ONE honesty-note visibility rule — never recompute the pair rule in a tile.
 *  `campaign` carries whichever factCarriesCampaign is relevant to the walk being annotated:
 *  state.campaign's own field (ratio-aware since T3) for a same-kpi walk, or a compare arm's own
 *  pair-rule bothCarry (useCompareFilterDims, T4) for a DIFFERENT compare kpi's ratio pair.
 *  factCarriesCampaign===true (exact resolution, incl. the eventual light-up) needs no caveat —
 *  this is how the note retires itself once both component facts carry the exact Campaign dim.
 *
 *  I2/M8: `appliedFilters` is the walk's actually-applied attribution set (page tiles pass
 *  state.campaign.resolvedFilters; compare/arm seams pass the campaign-resolved set they pruned) —
 *  NOT the raw predicate, so the note never names a filter the union prune dropped. Empty is a valid
 *  input (window-only campaign); the note renders the window-only variant. */
export function campaignRatioScopeFor(
  isRatio: boolean,
  campaign: Pick<ActiveCampaign, 'label' | 'factCarriesCampaign'> | null,
  appliedFilters: Record<string, string[]>,
): CampaignRatioScope | null {
  if (!isRatio || !campaign || campaign.factCarriesCampaign) return null
  return { campaignLabel: campaign.label, appliedFilters }
}

function toNoteInput(
  a: Extract<FilterApplicability, { status: 'partial' | 'empty' }>,
  labelFor: (dim: string, value: string) => string,
): ApplicabilityNoteInput {
  if (a.status === 'empty') return { status: 'empty', inapplicableDims: a.inapplicableDims }
  const attributed = new Set(Object.values(a.byDim).flat())
  return {
    status: 'partial',
    expectedCount: a.expectedCount,
    byDimLabels: Object.fromEntries(
      Object.entries(a.byDim).map(([dim, gs]) => [dim, gs.map((g) => labelFor(a.groupDim, g))]),
    ),
    unattributedLabels: a.dropped
      .filter((g) => !attributed.has(g))
      .map((g) => labelFor(a.groupDim, g)),
  }
}

export function FilterApplicabilityNote({
  state,
  factTable,
  kpi,
  filters,
  campaignRatioScope,
}: {
  state: ReviewPageState
  /** FU-m: override to evaluate a SIBLING walk (e.g. the driver arm's fact+kpi). Defaults to state.* */
  factTable?: string
  kpi?: string
  /** T4b fix-wave (spec §3.3): the filters the ANNOTATED WALK fired with. Defaults to the page
   *  walk's state.baseParams.filter_dims (T2 campaign-merged) — never the pre-merge state.filters,
   *  which under C3's identity-only URL omits an active campaign's resolved predicate and would
   *  leave campaign-caused partial groups silently unexplained (D-C3-2). Sibling walks (driver
   *  arms) pass their own fact-resolved set. */
  filters?: Record<string, string[]>
  /** C3b Task 5 (spec D-C3b-1): set via campaignRatioScopeFor when this note annotates a RATIO
   *  walk resolved via attribution filters under an active campaign (not the exact Campaign dim)
   *  — appends the honesty sentence so the figures don't read as campaign-exact. Renders
   *  independently of `applicability` (it isn't about dropped groups). null/absent = today's
   *  rendering byte-identical. */
  campaignRatioScope?: CampaignRatioScope | null
}) {
  const ft = factTable ?? state.factTable
  const k = kpi ?? state.kpi
  const applicability = useFilterApplicability({
    factTable: ft,
    kpi: k,
    groupBy: state.groupBy,
    filters: filters ?? state.baseParams.filter_dims,
  })
  const valueLabelFor = useDimValueLabelFor(ft)
  const applicabilityLines =
    applicability.status === 'ok' || applicability.status === 'unknown'
      ? []
      : buildApplicabilityNoteLines(toNoteInput(applicability, valueLabelFor))
  // I2/M8: render the applied attribution set. When it summarizes to nothing (window-only campaign,
  // or every attribution dim pruned), use the window-only variant so the note never shows "()".
  let ratioScopeLine: string | null = null
  if (campaignRatioScope) {
    const predicateSummary = buildContextCaption({
      filters: campaignRatioScope.appliedFilters,
      groupBy: [],
      valueLabelFor,
    }).join(', ')
    ratioScopeLine = predicateSummary
      ? campaignRatioScopeNote(campaignRatioScope.campaignLabel, predicateSummary)
      : campaignRatioScopeWindowOnlyNote(campaignRatioScope.campaignLabel)
  }
  const lines = ratioScopeLine ? [...applicabilityLines, ratioScopeLine] : applicabilityLines
  if (lines.length === 0) return null
  return (
    <Alert severity="warning" sx={{ py: 0 }}>
      {lines.map((line, i) => (
        <span key={i} style={{ display: 'block' }}>
          {line}
        </span>
      ))}
    </Alert>
  )
}
